import { skipToken } from "@reduxjs/toolkit/query";
import type { AutomationSettings } from "@repo/app-types/entities/automation";
import {
  applyAutomationConfigurationProposal,
  type AutomationConfiguration,
} from "@repo/app-types/entities/automationProposal";
import {
  copilotProposalIdSchema,
  type CopilotProposalId,
} from "@repo/app-types/entities/copilotProposal";
import { FieldError } from "@repo/ui/field/fieldError";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { useSearchParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { useUpdateAutomationMutation } from "../../api/entities/automation.js";
import {
  useGetPendingAutomationProposalQuery,
  useResolveProposalMutation,
} from "../../api/entities/copilotProposal.js";
import { isConflictQueryError } from "../../api/queryErrors.js";
import { CopilotProposalBanner } from "../../components/recordingAssessmentEditor/copilotProposalBanner.js";
import { ResourceSaveFooter } from "../../components/resourceSaveFooter.js";
import { AutomationConfigForm } from "../../features/automations/automationConfigForm.js";
import { AUTOMATION_PROPOSAL_QUERY_PARAM } from "../../features/automations/automationEditorUrlState.js";
import { CoachingPlaybookConflictModal } from "../../features/automations/coachingPlaybookConflictModal.js";
import { useAutomationFieldsContext } from "../../features/automations/useAutomationFieldsContext.js";
import { checkIsAutomationConfigDirty } from "./automationConfigDirty.js";
import { useAutomationDetailOutletContext } from "./automationDetailPage.js";

/** A pending `update_automation` proposal, compiled onto the draft it is laid over. */
interface AutomationProposalPreview {
  id: CopilotProposalId;
  configuration: AutomationConfiguration;
}

export const AutomationOverviewTab: FC = () => {
  const { automation, reportHasUnsavedChanges, reportIsShowingProposal } =
    useAutomationDetailOutletContext();

  const {
    isReadOnly,
    playbookAndVoiceAgentOptions,
    arePlaybooksLoading,
    hasLoadedAllPlaybookOptions,
    areRecipientOptionsLoading,
    teammateOptions,
    editorAndReviewerTeammateOptions,
    groupOptions,
    managerTeammateIds,
    coachingPlaybookIdsInUse,
  } = useAutomationFieldsContext();
  const [updateAutomation, { isLoading: isUpdating }] =
    useUpdateAutomationMutation();
  const [resolveProposal, { isLoading: isResolvingProposal }] =
    useResolveProposalMutation();

  const [name, setName] = useState(automation.name);
  const [settings, setSettings] = useState<AutomationSettings>(
    automation.settings,
  );
  // A proposal is shown in place of the draft, which is never written to until
  // the proposal is applied, so discarding one needs no restore.
  const [proposal, setProposal] = useState<AutomationProposalPreview | null>(
    null,
  );
  const displayedConfiguration = proposal?.configuration ?? { name, settings };
  const [hasCoachingConflict, setHasCoachingConflict] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // A unique id for this editing session, generated when the config screen
  // loads. Stamped on every revision so revisions can later be grouped by
  // session and conflicting edits detected. Not surfaced to users yet.
  const sessionIdRef = useRef(uuidv4());

  // Compare the normalized value the server stores so whitespace-only edits do
  // not keep the explicit-save footer visible.
  const effectiveName = name.trim() || automation.name;

  const isDirty = checkIsAutomationConfigDirty(
    { name: effectiveName, settings },
    { name: automation.name, settings: automation.settings },
  );
  const isShowingProposal = proposal !== null;

  // One-shot deep link from an Ask Alpharun proposal card (?proposalId=...):
  // the proposal is laid over the form as a read-only preview. A proposal for
  // another automation loads nothing.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkProposalId = copilotProposalIdSchema.safeParse(
    searchParams.get(AUTOMATION_PROPOSAL_QUERY_PARAM),
  ).data;
  const {
    currentData: deepLinkProposal,
    isError: isDeepLinkProposalUnavailable,
  } = useGetPendingAutomationProposalQuery(deepLinkProposalId ?? skipToken);
  const clearDeepLinkProposalId = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(AUTOMATION_PROPOSAL_QUERY_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  useEffect(() => {
    if (deepLinkProposalId && isDeepLinkProposalUnavailable) {
      clearDeepLinkProposalId();
      setSaveError("This suggestion is no longer available.");
    }
  }, [
    deepLinkProposalId,
    isDeepLinkProposalUnavailable,
    clearDeepLinkProposalId,
  ]);
  useEffect(() => {
    // A people change compiles against who manages a group, so it waits for
    // the groups to load.
    if (
      areRecipientOptionsLoading ||
      !deepLinkProposalId ||
      !deepLinkProposal ||
      deepLinkProposal.id !== deepLinkProposalId
    ) {
      return;
    }
    clearDeepLinkProposalId();
    const { data } = deepLinkProposal;
    if (
      deepLinkProposal.resource_id !== automation.id ||
      data.kind !== "update_automation"
    ) {
      return;
    }
    try {
      setProposal({
        id: deepLinkProposal.id,
        configuration: applyAutomationConfigurationProposal(
          { name, settings },
          data.configuration,
          { managerTeammateIds },
        ),
      });
      setSaveError(null);
    } catch {
      setSaveError("This suggestion no longer fits this automation.");
    }
  }, [
    areRecipientOptionsLoading,
    deepLinkProposalId,
    deepLinkProposal,
    clearDeepLinkProposalId,
    automation.id,
    name,
    settings,
    managerTeammateIds,
  ]);

  const handleSave = async () => {
    setSaveError(null);
    try {
      await updateAutomation({
        id: automation.id,
        name: effectiveName,
        settings,
        session_id: sessionIdRef.current,
      }).unwrap();
    } catch (error) {
      if (isConflictQueryError(error)) {
        setSettings(automation.settings);
        setName(automation.name);
        setHasCoachingConflict(true);
        return;
      }
      setSaveError("Your changes couldn't be saved. Please try again.");
    }
  };

  const handleCancel = () => {
    setName(automation.name);
    setSettings(automation.settings);
    setSaveError(null);
  };

  // Applying a suggestion resolves it and puts it into the draft. Save then
  // writes the draft like any other edit.
  const handleApplyProposal = async () => {
    if (!proposal) {
      return;
    }
    setSaveError(null);
    try {
      await resolveProposal({
        proposalId: proposal.id,
        status: "applied",
      }).unwrap();
    } catch (error) {
      if (!isConflictQueryError(error)) {
        setSaveError("Failed to apply the suggestion. Please try again.");
        return;
      }
      // A proposal that has moved on can no longer be applied.
      setSaveError("This suggestion is no longer available.");
      setProposal(null);
      return;
    }
    setName(proposal.configuration.name);
    setSettings(proposal.configuration.settings);
    setProposal(null);
  };

  const handleDiscardProposal = async () => {
    if (!proposal) {
      return;
    }
    setSaveError(null);
    try {
      await resolveProposal({
        proposalId: proposal.id,
        status: "discarded",
      }).unwrap();
    } catch (error) {
      // A proposal that has moved on is already not being applied, so the
      // preview is dropped either way.
      if (!isConflictQueryError(error)) {
        setSaveError("Failed to discard the suggestion. Please try again.");
        return;
      }
      setSaveError("This suggestion is no longer available.");
    }
    setProposal(null);
  };

  useEffect(() => {
    // Leaving with a suggestion on screen is not guarded: it stays pending in
    // the chat.
    reportHasUnsavedChanges(isDirty && !isShowingProposal);
    reportIsShowingProposal(isShowingProposal);
    return () => {
      reportHasUnsavedChanges(false);
      reportIsShowingProposal(false);
    };
  }, [
    isDirty,
    isShowingProposal,
    reportHasUnsavedChanges,
    reportIsShowingProposal,
  ]);

  return (
    <>
      {proposal ? (
        <CopilotProposalBanner
          className="mx-0 mt-0 rounded-lg"
          message="Showing changes Ask Alpharun suggests"
          discardLabel="Discard changes"
          confirmLabel="Apply changes"
          onDiscard={() => {
            void handleDiscardProposal();
          }}
          onConfirm={() => {
            void handleApplyProposal();
          }}
          isDisabled={isReadOnly || isResolvingProposal}
        />
      ) : null}

      <AutomationConfigForm
        templateId={automation.template_id}
        isReadOnly={isReadOnly || isShowingProposal}
        playbookAndVoiceAgentOptions={playbookAndVoiceAgentOptions}
        arePlaybooksLoading={arePlaybooksLoading}
        hasLoadedAllPlaybookOptions={hasLoadedAllPlaybookOptions}
        areRecipientOptionsLoading={areRecipientOptionsLoading}
        teammateOptions={teammateOptions}
        editorAndReviewerTeammateOptions={editorAndReviewerTeammateOptions}
        groupOptions={groupOptions}
        managerTeammateIds={managerTeammateIds}
        coachingPlaybookIdsInUse={coachingPlaybookIdsInUse}
        name={displayedConfiguration.name}
        settings={displayedConfiguration.settings}
        // A suggestion reads against the draft it is laid over, and an edit of
        // your own against what is saved.
        savedConfiguration={proposal ? { name, settings } : automation}
        onNameChange={setName}
        onSettingsChange={setSettings}
        onBlur={() => {
          setName(effectiveName);
        }}
      />

      {saveError ? <FieldError>{saveError}</FieldError> : null}

      {isDirty && !isShowingProposal ? (
        <ResourceSaveFooter
          className="sticky bottom-0 z-10 -mx-4 mt-auto -mb-4 sm:-mx-6 sm:-mb-6"
          onCancel={handleCancel}
          onSave={() => {
            void handleSave();
          }}
          isSaving={isUpdating}
        />
      ) : null}

      <CoachingPlaybookConflictModal
        isOpen={hasCoachingConflict}
        onClose={() => {
          setHasCoachingConflict(false);
        }}
      />
    </>
  );
};
